import React, { useState, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse,
} from '@mui/material'
import {
  Upload as UploadIcon,
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { productService } from '../../services/productService'

interface ImportError {
  row: number
  field?: string
  message: string
}

interface ImportResult {
  total_rows: number
  successful_imports: number
  failed_imports: number
  errors: ImportError[]
}

interface ProductImportDialogProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

const ProductImportDialog: React.FC<ProductImportDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [showErrors, setShowErrors] = useState(false)

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type
      const allowedTypes = ['text/csv', 'application/vnd.ms-excel']
      if (!allowedTypes.includes(file.type) && !file.name.endsWith('.csv')) {
        enqueueSnackbar('Please select a CSV file', { variant: 'error' })
        return
      }

      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024
      if (file.size > maxSize) {
        enqueueSnackbar('File size must be less than 10MB', { variant: 'error' })
        return
      }

      setSelectedFile(file)
      setImportResult(null)
    }
  }

  const handleImport = async () => {
    if (!selectedFile) {
      enqueueSnackbar('Please select a file', { variant: 'warning' })
      return
    }

    setIsImporting(true)
    try {
      const result = await productService.bulkImport(selectedFile)
      setImportResult(result)

      if (result.failed_imports === 0) {
        enqueueSnackbar(
          `Successfully imported ${result.successful_imports} products`,
          { variant: 'success' }
        )
        if (onSuccess) {
          onSuccess()
        }
      } else {
        enqueueSnackbar(
          `Imported ${result.successful_imports} products with ${result.failed_imports} errors`,
          { variant: 'warning' }
        )
      }
    } catch (error: any) {
      console.error('Import error:', error)
      enqueueSnackbar(
        error.response?.data?.detail || 'Failed to import products',
        { variant: 'error' }
      )
    } finally {
      setIsImporting(false)
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setImportResult(null)
    setShowErrors(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    onClose()
  }

  const handleReset = () => {
    setSelectedFile(null)
    setImportResult(null)
    setShowErrors(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Products</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          <Alert severity="info">
            <Typography variant="body2" gutterBottom>
              <strong>CSV Format Requirements:</strong>
            </Typography>
            <Typography variant="caption" component="div">
              • Include headers: name, sku, mrp, sale_price, wholesale_price, purchase_price, hsn_code, gst_rate
            </Typography>
            <Typography variant="caption" component="div">
              • All prices should be numeric values
            </Typography>
            <Typography variant="caption" component="div">
              • SKU must be unique for each product
            </Typography>
            <Typography variant="caption" component="div">
              • Maximum file size: 10MB
            </Typography>
          </Alert>

          {!importResult && (
            <Box
              sx={{
                border: '2px dashed',
                borderColor: selectedFile ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                bgcolor: selectedFile ? 'primary.50' : 'grey.50',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.50',
                },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv,application/vnd.ms-excel"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              {selectedFile ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(selectedFile.size / 1024).toFixed(2)} KB
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleReset()
                    }}
                    sx={{ mt: 2 }}
                  >
                    Choose Different File
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom>
                    Click to select CSV file
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or drag and drop here
                  </Typography>
                </>
              )}
            </Box>
          )}

          {isImporting && (
            <Box>
              <Typography variant="body2" gutterBottom>
                Importing products...
              </Typography>
              <LinearProgress />
            </Box>
          )}

          {importResult && (
            <Box>
              <Alert
                severity={importResult.failed_imports === 0 ? 'success' : 'warning'}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">
                  <strong>Import Complete</strong>
                </Typography>
                <Typography variant="caption" component="div">
                  Total rows: {importResult.total_rows}
                </Typography>
                <Typography variant="caption" component="div">
                  Successful: {importResult.successful_imports}
                </Typography>
                <Typography variant="caption" component="div">
                  Failed: {importResult.failed_imports}
                </Typography>
              </Alert>

              {importResult.errors.length > 0 && (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" color="error">
                      Import Errors ({importResult.errors.length})
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setShowErrors(!showErrors)}
                    >
                      {showErrors ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>

                  <Collapse in={showErrors}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Row</TableCell>
                            <TableCell>Field</TableCell>
                            <TableCell>Error Message</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {importResult.errors.slice(0, 10).map((error, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Chip label={error.row} size="small" color="error" />
                              </TableCell>
                              <TableCell>
                                {error.field || 'N/A'}
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {error.message}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {importResult.errors.length > 10 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Showing first 10 of {importResult.errors.length} errors
                      </Typography>
                    )}
                  </Collapse>
                </Box>
              )}

              <Button
                variant="outlined"
                onClick={handleReset}
                fullWidth
                sx={{ mt: 2 }}
              >
                Import Another File
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {importResult ? 'Close' : 'Cancel'}
        </Button>
        {!importResult && (
          <Button
            onClick={handleImport}
            variant="contained"
            startIcon={isImporting ? <LinearProgress /> : <UploadIcon />}
            disabled={!selectedFile || isImporting}
          >
            {isImporting ? 'Importing...' : 'Import'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ProductImportDialog
